import { Badge, Flex, Typography } from 'antd';
import FilterCard from './FilterCard';

const { Text } = Typography;

/**
 * Renders the "FILTERS" section: a header with status/count or hint text,
 * followed by a list of FilterCard components.
 *
 * Must be rendered inside an antd Form — each FilterCard renders a RangeSliderField
 * (Form.Item) keyed by filter.id, so the Form owns the values.
 *
 * When `isDirty` is provided (even `false`), the header shows a save-status badge
 * on the left and the live detection count on the right. When omitted, the header
 * falls back to the "empty = no limit" hint text — keeping the component reusable
 * in contexts without save state.
 *
 * @param {{
 *   filters: Array<{ id, label, unit, min, max, clamped?, disabled? }>,
 *   activeFilters: object,
 *   dirtyFilters: object,
 *   showSlider?: boolean,
 *   isDirty?: boolean,
 *   shownCount?: number,
 *   totalCount?: number,
 *   histogramValues?: { [filterId]: Array<number> },
 *   onClear: (filterId: string) => void,
 * }} props
 */
const FilterList = ({
	filters,
	activeFilters,
	dirtyFilters = {},
	showSlider = true,
	isDirty,
	shownCount,
	totalCount,
	histogramValues,
	onClear,
}) => {
	return (
		<Flex vertical gap={12}>
			{/* Section header */}
			<Flex align="center" justify="space-between" style={{ padding: '0 16px' }}>
				<Flex align="center" gap={8}>
					<Text strong style={{ fontSize: '0.85rem', letterSpacing: 0.5 }}>
						FILTERS
					</Text>
					{isDirty && (
						<Badge
							status="warning"
							text={<Text style={{ fontSize: '0.75rem' }}>Unsaved</Text>}
						/>
					)}
				</Flex>

				<Text type="secondary" style={{ fontSize: '0.75rem' }}>
					{isDirty !== undefined
						? `${shownCount} / ${totalCount} detections`
						: 'empty = no limit'}
				</Text>
			</Flex>

			{/* Filter cards */}
			<Flex vertical gap={8}>
				{filters.map(filter => (
					<FilterCard
						key={filter.id}
						filter={filter}
						isActive={activeFilters[filter.id] ?? false}
						isDirty={dirtyFilters[filter.id] ?? false}
						showSlider={showSlider}
						histogramValues={histogramValues?.[filter.id]}
						onClear={() => onClear(filter.id)}
					/>
				))}
			</Flex>
		</Flex>
	);
};

export default FilterList;
